import { useNovel } from '@/context/novel-context';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

type CollaborationListReadersProps = {
  currentUserUid?: string;
  isCurrentUserOwner: boolean;
  isUpdatingReader: boolean;
  updatingMemberId: string | null;
  onRemoveReader: (id: string) => Promise<void>;
  onRemovePendingReader: (email: string) => Promise<void>;
};

export function CollaborationListReaders({
  currentUserUid,
  isCurrentUserOwner,
  isUpdatingReader,
  updatingMemberId,
  onRemoveReader,
  onRemovePendingReader,
}: CollaborationListReadersProps) {
  const { t } = useTranslation();
  const { novel } = useNovel();
  if (!novel.id) return null;

  const readersList = Object.entries(novel.readers || {});
  const hasReaders = readersList.length > 0 || (novel.pendingReaders && novel.pendingReaders.length > 0);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
        {t('dashboard.settings.readers')}
        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
          ({readersList.length + (novel.pendingReaders?.length || 0)})
        </Typography>
      </Typography>

      {!hasReaders ? (
        <Box sx={{ p: 2.5, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.settings.no_readers')}
          </Typography>
        </Box>
      ) : (
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0 }}>
          {readersList.map(([readerId, displayName]) => {
            const readerName = readerId === currentUserUid
              ? t('dashboard.settings.you')
              : displayName;
            const readerSub = t('dashboard.settings.reader_sub');

            return (
              <ListItem
                key={readerId}
                secondaryAction={
                  isCurrentUserOwner && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      size="small"
                      color="error"
                      onClick={() => onRemoveReader(readerId)}
                      disabled={isUpdatingReader}
                      sx={{
                        opacity: 0.6,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 1, bgcolor: 'error.lighter' }
                      }}
                    >
                      {updatingMemberId === readerId ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <CloseIcon fontSize="small" />
                      )}
                    </IconButton>
                  )
                }
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateX(3px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    borderColor: 'secondary.light',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`, color: '#fff' }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'sans-serif' }}>
                        {readerName}
                      </Typography>
                      {readerId === currentUserUid && (
                        <Chip label={t('dashboard.settings.you_badge')} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Stack>
                  }
                  secondary={readerSub}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
            );
          })}

          {novel.pendingReaders?.map((email) => (
            <ListItem
              key={email}
              secondaryAction={
                isCurrentUserOwner && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    color="error"
                    onClick={() => onRemovePendingReader(email)}
                    disabled={isUpdatingReader}
                    sx={{
                      opacity: 0.6,
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 1, bgcolor: 'error.lighter' }
                    }}
                  >
                    {updatingMemberId === email ? (
                      <CircularProgress size={16} color="error" />
                    ) : (
                      <CloseIcon fontSize="small" />
                    )}
                  </IconButton>
                )
              }
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider',
                opacity: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateX(3px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  borderColor: 'secondary.light',
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.action.disabledBackground} 0%, ${theme.palette.secondary.light} 100%)`, color: 'text.secondary' }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontStyle: 'italic' }}>
                      {email}
                    </Typography>
                    <Chip
                      label={t('dashboard.settings.pending_badge')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: 'action.selected',
                        color: 'secondary.main',
                        fontWeight: 600
                      }}
                    />
                  </Stack>
                }
                secondary={t('dashboard.settings.reader_invited_sub')}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
export default CollaborationListReaders;
